import { formatPhone } from "@/lib/phone";
import { DataError } from "@/components/data-error";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { queryDocs } from "@/integrations/firebase/data";
import { useCallsSince, hourFloorIso } from "@/hooks/use-calls-since";
import { recordingUrl } from "@/integrations/firebase/recording";
import { useAuth } from "@/hooks/use-auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatDuration, formatWhen, routingOutcomeLabel, routingOutcomeColor, isReallyLive } from "@/lib/calls";
import { CallStatus, isAnsweredStatus } from "@/components/call-bits";
import { cn } from "@/lib/utils";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Download, Search, X, Play, ChevronRight } from "lucide-react";
import { IvrCallFlow } from "@/components/ivr-call-flow";
import { buildIvrTree, buildIvrTreeFromNodes } from "@/lib/ivr-flow";
import { useMainMenuLabels, labelForDigit } from "@/lib/ivr-labels";

export const Route = createFileRoute("/_app/ivr")({
  head: () => ({ meta: [{ title: "IVR & routing — EESWEB" }] }),
  component: IvrDashboard,
});

type CallRow = {
  id: string; started_at: string | null; from_number: string | null; direction?: string | null;
  smartflo_call_id: string; main_menu_selection: string | null;
  branch_id: string | null; department_id: string | null; employee_id: string | null;
  workshop_type: string | null; duration_sec: number | null; billable_sec: number | null;
  status: string; recording_url: string | null; recording_available: boolean;
  agent_name: string | null; agent_number: string | null; ivr_path: string | null;
  destination: string | null; timegroup_name: string | null;
  auto_attendant_name: string | null; caller_landing: string | null;
  routing_outcome: string | null; is_live?: boolean | null;
};

const OUTCOMES = ["routed", "fallback", "unmatched", "unanswered", "failed"] as const;
const OUTCOME_BAR: Record<string, string> = {
  routed: "bg-success", fallback: "bg-warning", unmatched: "bg-muted-foreground/50", unanswered: "bg-destructive", failed: "bg-destructive/60",
};
const OUTCOME_HINT: Record<string, string> = {
  routed: "Reached the branch or department the caller chose",
  fallback: "Went to the fallback number instead",
  unmatched: "Key path isn't in the routing map",
  unanswered: "Rang but no one picked up",
  failed: "Smartflo reported a failure",
};

function IvrDashboard() {
  const { effectiveClientId } = useAuth();
  const [days, setDays] = useState("30");
  const [search, setSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");
  const [empFilter, setEmpFilter] = useState("all");
  const [menuFilter, setMenuFilter] = useState("all");
  const [outcomeFilter, setOutcomeFilter] = useState("all");
  const [showAll, setShowAll] = useState(false);

  const since = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - parseInt(days, 10)); return d.toISOString();
  }, [days]);

  const { data: branches } = useQuery({
    queryKey: ["ivr-branches", effectiveClientId],
    queryFn: async () => {
      const rows = await queryDocs<{ id: string; name: string; sort_order?: number }>("branches", { clientId: effectiveClientId });
      return rows.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    },
  });
  const { data: departments } = useQuery({
    queryKey: ["ivr-depts", effectiveClientId],
    queryFn: async () => {
      const rows = await queryDocs<{ id: string; name: string; kind: string; sort_order?: number }>("departments", { clientId: effectiveClientId });
      return rows.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    },
  });
  const { data: employees } = useQuery({
    queryKey: ["ivr-emps", effectiveClientId],
    queryFn: () => queryDocs<{ id: string; name: string; mobile: string; branch_id: string; department_id: string }>("employees", { clientId: effectiveClientId }),
  });

  const { data: ivrNodes } = useQuery({
    queryKey: ["ivr-nodes-flow", effectiveClientId],
    queryFn: async () => {
      const rows = await queryDocs<{ id: string; parent_id: string | null; digit: string; key_path: string; label: string; is_active: boolean; phone_number: string | null; sort_order?: number }>("ivr_menu_nodes", { clientId: effectiveClientId });
      return rows.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    },
  });
  const { data: ivrRoutes } = useQuery({
    queryKey: ["ivr-routes-flow", effectiveClientId],
    queryFn: async () => {
      const rows = await queryDocs<{ key_path: string; label: string }>("ivr_routes", { clientId: effectiveClientId });
      return rows.sort((a, b) => a.key_path.localeCompare(b.key_path));
    },
  });
  // Prefer the explicit node tree; fall back to legacy route rows for any
  // client not yet migrated to ivr_menu_nodes.
  const flowTree = useMemo(
    () => (ivrNodes && ivrNodes.length > 0 ? buildIvrTreeFromNodes(ivrNodes) : buildIvrTree(ivrRoutes ?? [])),
    [ivrNodes, ivrRoutes],
  );
  const { data: menuLabels } = useMainMenuLabels(effectiveClientId);
  // Distinct top-level menu digits actually present for this client.
  const menuDigits = useMemo(() => {
    const set = new Set<string>();
    for (const n of ivrNodes ?? []) if (!n.parent_id) set.add(n.digit);
    if (set.size === 0) for (const r of ivrRoutes ?? []) set.add(r.key_path.split(">")[0]);
    return [...set].sort((a, b) => Number(a) - Number(b));
  }, [ivrNodes, ivrRoutes]);

  // Live listener from whichever is earlier: the picked range or the start of the
  // month, so "Today" and "This month" stay correct whatever range is picked.
  const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
  const listenFrom = hourFloorIso(Date.now() - Math.min(new Date(since).getTime(), monthStart.getTime()));
  const { data: rangeCalls, isLoading, error: callsError } = useCallsSince<CallRow>(effectiveClientId, listenFrom);
  // Only incoming calls go through the IVR; staff call-backs would show up as "no choice made".
  const allCalls = useMemo(() => rangeCalls?.filter((c) => c.direction !== "outbound"), [rangeCalls]);
  // No cap: every figure on this page must count every call in the range.
  const calls = useMemo(
    () => allCalls?.filter((c) => c.started_at && c.started_at >= since),
    [allCalls, since],
  );

  const branchMap = useMemo(() => new Map((branches ?? []).map((b) => [b.id, b.name])), [branches]);
  const deptMap = useMemo(() => new Map((departments ?? []).map((d) => [d.id, d.name])), [departments]);
  const empMap = useMemo(() => new Map((employees ?? []).map((e) => [e.id, e])), [employees]);

  const filtered = useMemo(() => {
    return (calls ?? []).filter((c) => {
      if (branchFilter !== "all" && c.branch_id !== branchFilter) return false;
      if (deptFilter !== "all" && c.department_id !== deptFilter) return false;
      if (empFilter !== "all" && c.employee_id !== empFilter) return false;
      if (menuFilter !== "all" && c.main_menu_selection !== menuFilter) return false;
      if (outcomeFilter !== "all" && c.routing_outcome !== outcomeFilter) return false;
      if (search) {
        // Spaces ignored, so "98111 00002" (how numbers are shown) finds "+919811100002".
        const s = search.toLowerCase().replace(/\s+/g, "");
        const txt = [c.from_number, c.agent_name, c.smartflo_call_id, c.ivr_path].filter(Boolean).join("|").toLowerCase().replace(/\s+/g, "");
        if (!txt.includes(s)) return false;
      }
      return true;
    });
  }, [calls, branchFilter, deptFilter, empFilter, menuFilter, outcomeFilter, search]);

  const all = calls ?? [];
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const month = new Date(); month.setDate(1); month.setHours(0, 0, 0, 0);
  const totals = {
    period: all.length,
    today: (allCalls ?? []).filter((c) => c.started_at && new Date(c.started_at) >= today).length,
    month: (allCalls ?? []).filter((c) => c.started_at && new Date(c.started_at) >= month).length,
  };

  const outcomeCounts = useMemo(() => {
    const m: Record<string, number> = {};
    // A call still in progress has no final outcome yet.
    for (const c of all) if (c.routing_outcome && !isReallyLive(c)) m[c.routing_outcome] = (m[c.routing_outcome] ?? 0) + 1;
    return m;
  }, [all]);
  const outcomeTotal = OUTCOMES.reduce((s, o) => s + (outcomeCounts[o] ?? 0), 0);
  // Calls that didn't cleanly reach their intended target.
  const needsAttention = outcomeTotal - (outcomeCounts.routed ?? 0);

  const menuData = menuDigits.map((d) => ({
    key: d, name: labelForDigit(menuLabels, d), calls: all.filter((c) => c.main_menu_selection === d).length,
  }));
  const noMenuChoice = all.filter((c) => !c.main_menu_selection).length;

  const rank = (pairs: Map<string, number>) =>
    [...pairs.entries()].map(([name, calls]) => ({ name, calls })).filter((r) => r.calls > 0).sort((a, b) => b.calls - a.calls);
  const countBy = (key: (c: CallRow) => string | null | undefined) => {
    const m = new Map<string, number>();
    for (const c of all) { const k = key(c); if (k) m.set(k, (m.get(k) ?? 0) + 1); }
    return m;
  };
  const rankings = {
    branches: rank(countBy((c) => (c.branch_id ? branchMap.get(c.branch_id) : null))),
    departments: rank(countBy((c) => (c.department_id ? deptMap.get(c.department_id) : null))),
    employees: rank(countBy((c) => (c.employee_id ? empMap.get(c.employee_id)?.name : null))),
    destinations: rank(countBy((c) => c.destination ?? c.timegroup_name)),
    timegroups: rank(countBy((c) => c.timegroup_name)),
  };

  // Calls per day across the selected range (zero-filled so gaps are visible).
  const trendData = useMemo(() => {
    const n = Math.min(parseInt(days, 10), 90);
    const counts = new Map<string, number>();
    for (const c of all) if (c.started_at) {
      const d = new Date(c.started_at); const k = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    return Array.from({ length: n }, (_, i) => {
      const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - (n - 1 - i));
      return {
        date: d.toLocaleDateString([], { day: "numeric", month: "short" }),
        calls: counts.get(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`) ?? 0,
      };
    });
  }, [all, days]);

  const activeFilters = [search, branchFilter, deptFilter, empFilter, menuFilter, outcomeFilter].filter((v) => v && v !== "all").length;
  const clearFilters = () => {
    setSearch(""); setBranchFilter("all"); setDeptFilter("all"); setEmpFilter("all"); setMenuFilter("all"); setOutcomeFilter("all");
  };
  const routeLabel = (c: CallRow) =>
    [c.branch_id && branchMap.get(c.branch_id), c.workshop_type && `${c.workshop_type.toUpperCase()} workshop`, c.department_id && deptMap.get(c.department_id)]
      .filter(Boolean).join(" › ");

  // xlsx is ~400 KB: load it only when someone actually exports.
  const exportExcel = async () => {
    const XLSX = await import("xlsx");
    const rows = filtered.map((c) => {
      const emp = c.employee_id ? empMap.get(c.employee_id) : null;
      return {
        "Date & Time": c.started_at ? new Date(c.started_at).toLocaleString() : "",
        "Mobile Number": c.from_number ?? "",
        "Call ID": c.smartflo_call_id,
        "Destination": c.destination ?? c.timegroup_name ?? "",
        "Main Menu": labelForDigit(menuLabels, c.main_menu_selection ?? "") ?? c.main_menu_selection ?? "",
        "Branch": c.branch_id ? branchMap.get(c.branch_id) ?? "" : "",
        "Workshop Type": c.workshop_type ?? "",
        "Department": c.department_id ? deptMap.get(c.department_id) ?? "" : "",
        "Employee": emp?.name ?? c.agent_name ?? "",
        "Employee Mobile": emp?.mobile ?? c.agent_number ?? "",
        "Duration": formatDuration(c.billable_sec ?? c.duration_sec),
        "Status": c.status,
        "Routing Result": c.routing_outcome ? (routingOutcomeLabel[c.routing_outcome] ?? c.routing_outcome) : "",
        "Recording": c.recording_url ?? "",
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Calls");
    XLSX.writeFile(wb, `ivr-calls-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const visibleRows = showAll ? filtered : filtered.slice(0, 50);
  const rangeLabel = { "1": "last 24 hours", "7": "last 7 days", "30": "last 30 days", "90": "last 90 days", "365": "last 12 months" }[days];

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <DataError error={callsError} />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">IVR &amp; routing</h1>
          <p className="text-sm text-muted-foreground">What callers chose in the menu, and where their calls ended up. Incoming calls only.</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="h-10 w-40 bg-card" aria-label="Date range"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Last 24 hours</SelectItem>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last 12 months</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary */}
      <section className="grid grid-cols-2 overflow-hidden rounded-2xl border bg-card shadow-soft md:grid-cols-4 [&>div]:border-border [&>div:nth-child(2n)]:border-l md:[&>div:not(:first-child)]:border-l [&>div:nth-child(n+3)]:border-t md:[&>div:nth-child(n+3)]:border-t-0">
        <Figure label={`Calls, ${rangeLabel}`} value={isLoading ? null : totals.period} />
        <Figure label="Today" value={isLoading ? null : totals.today} />
        <Figure label="This month" value={isLoading ? null : totals.month} />
        <div className="p-4 md:p-5">
          <div className="text-xs text-muted-foreground">Need attention</div>
          {isLoading ? <Skeleton className="mt-1.5 h-7 w-12" /> : (
            <button
              onClick={() => document.getElementById("ivr-outcomes")?.scrollIntoView({ behavior: "smooth", block: "center" })}
              disabled={needsAttention === 0}
              className={cn("mt-0.5 font-display text-2xl font-semibold tabular tracking-tight", needsAttention > 0 ? "text-destructive hover:underline" : "text-foreground")}
            >
              {needsAttention}
            </button>
          )}
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Menu choices */}
        <section className="min-w-0 rounded-2xl border bg-card p-5 shadow-soft">
          <h2 className="text-sm font-semibold">What callers pressed</h2>
          <p className="mb-4 text-xs text-muted-foreground">Main-menu choice for each call. Tap one to filter the report.</p>
          {menuData.length === 0 ? (
            <EmptyNote>No IVR menu set up for this dealership yet.</EmptyNote>
          ) : (
            <RankList
              rows={[...menuData.map((m) => ({ name: m.name, calls: m.calls, key: m.key })), ...(noMenuChoice ? [{ name: "No choice made", calls: noMenuChoice, key: "" }] : [])]}
              total={all.length}
              active={menuFilter}
              onPick={(k) => k && setMenuFilter(menuFilter === k ? "all" : k)}
            />
          )}
        </section>

        {/* Routing outcomes */}
        <section id="ivr-outcomes" className="min-w-0 scroll-mt-20 rounded-2xl border bg-card p-5 shadow-soft">
          <h2 className="text-sm font-semibold">Routing outcome</h2>
          <p className="mb-4 text-xs text-muted-foreground">How calls fared against your routing map. This is reporting only; live routing stays in Smartflo.</p>
          {outcomeTotal === 0 ? (
            <EmptyNote>No outcomes recorded yet. New calls are tagged as they arrive.</EmptyNote>
          ) : (
            <>
              <div className="mb-4 flex h-2.5 overflow-hidden rounded-full bg-muted" role="img" aria-label="Routing outcome split">
                {OUTCOMES.map((o) => outcomeCounts[o] ? (
                  <span key={o} className={OUTCOME_BAR[o]} style={{ width: `${(outcomeCounts[o] / outcomeTotal) * 100}%` }} />
                ) : null)}
              </div>
              <ul className="space-y-1">
                {OUTCOMES.filter((o) => outcomeCounts[o]).map((o) => (
                  <li key={o}>
                    <button
                      onClick={() => setOutcomeFilter(outcomeFilter === o ? "all" : o)}
                      aria-pressed={outcomeFilter === o}
                      className={cn("flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-muted/60", outcomeFilter === o && "bg-muted")}
                    >
                      <span className={cn("size-2.5 shrink-0 rounded-[3px]", OUTCOME_BAR[o])} />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium">{routingOutcomeLabel[o]}</span>
                        <span className="block truncate text-xs text-muted-foreground">{OUTCOME_HINT[o]}</span>
                      </span>
                      <span className="text-sm font-semibold tabular">{outcomeCounts[o]}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>

      {/* Breakdown */}
      <section className="rounded-2xl border bg-card p-5 shadow-soft">
        <Tabs defaultValue="branches">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-semibold">Where calls went</h2>
            <div className="-mx-1 max-w-full overflow-x-auto px-1">
              <TabsList>
                <TabsTrigger value="branches">Branches</TabsTrigger>
                <TabsTrigger value="departments">Departments</TabsTrigger>
                <TabsTrigger value="employees">Employees</TabsTrigger>
                <TabsTrigger value="destinations">Destinations</TabsTrigger>
                <TabsTrigger value="timegroups">Time groups</TabsTrigger>
              </TabsList>
            </div>
          </div>
          {(Object.keys(rankings) as (keyof typeof rankings)[]).map((k) => (
            <TabsContent key={k} value={k} className="mt-0">
              {rankings[k].length === 0
                ? <EmptyNote>No calls have been matched to {k === "timegroups" ? "a time group" : k.replace(/s$/, "")} in this period.</EmptyNote>
                : <RankList rows={rankings[k].slice(0, 10)} total={all.length} />}
            </TabsContent>
          ))}
        </Tabs>
      </section>

      <section className="rounded-2xl border bg-card p-5 shadow-soft">
        <h2 className="mb-4 text-sm font-semibold">Calls per day</h2>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={trendData} margin={{ top: 4, right: 0, left: -24, bottom: 0 }} barCategoryGap="20%">
            <CartesianGrid vertical={false} stroke="var(--border)" />
            <XAxis dataKey="date" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={24} />
            <YAxis allowDecimals={false} stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip cursor={{ fill: "var(--muted)", opacity: 0.6 }} contentStyle={{ background: "var(--popover)", color: "var(--popover-foreground)", border: "1px solid var(--border)", borderRadius: 10, fontSize: 12 }} />
            <Bar dataKey="calls" name="Calls" fill="var(--primary)" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </section>

      {/* IVR call flow reference */}
      <IvrCallFlow nodes={flowTree} title="Your IVR menu" className="rounded-2xl shadow-soft" />

      {/* Call monitoring report */}
      <section id="ivr-report" className="scroll-mt-20 overflow-hidden rounded-2xl border bg-card shadow-soft">
        <div className="space-y-3 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-semibold">
              Call report <span className="font-normal text-muted-foreground tabular">· {filtered.length}</span>
            </h2>
            <div className="flex items-center gap-2">
              {activeFilters > 0 && (
                <button onClick={clearFilters} className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-primary hover:bg-primary/10">
                  <X className="size-3.5" /> Clear {activeFilters} {activeFilters === 1 ? "filter" : "filters"}
                </button>
              )}
              <Button variant="outline" size="sm" onClick={exportExcel} disabled={filtered.length === 0}>
                <Download className="size-4" /> Export Excel
              </Button>
            </div>
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input type="search" aria-label="Search the report" className="h-10 pl-9" placeholder="Search number, agent or call ID" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
            <FilterSelect label="Menu" value={menuFilter} onChange={setMenuFilter} all="All menu choices" options={menuDigits.map((d) => [d, labelForDigit(menuLabels, d)])} />
            <FilterSelect label="Outcome" value={outcomeFilter} onChange={setOutcomeFilter} all="All outcomes" options={OUTCOMES.map((o) => [o, routingOutcomeLabel[o]])} />
            <FilterSelect label="Branch" value={branchFilter} onChange={setBranchFilter} all="All branches" options={(branches ?? []).map((b) => [b.id, b.name])} />
            <FilterSelect label="Department" value={deptFilter} onChange={setDeptFilter} all="All departments" options={(departments ?? []).map((d) => [d.id, d.name])} />
            <FilterSelect label="Employee" value={empFilter} onChange={setEmpFilter} all="All employees" options={(employees ?? []).map((e) => [e.id, e.name])} className="col-span-2 lg:col-span-1" />
          </div>
        </div>

        <div className="hidden grid-cols-[7.5rem_minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1fr)_4rem_7.5rem_2rem] gap-4 border-y bg-muted/40 px-5 py-2.5 text-xs font-medium text-muted-foreground lg:grid">
          <span>Time</span><span>Caller</span><span>Routed to</span><span>Employee</span><span className="text-right">Talk</span><span>Outcome</span><span />
        </div>
        {isLoading ? (
          <div className="divide-y border-t">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="px-5 py-3.5"><Skeleton className="h-4 w-2/3" /></div>)}</div>
        ) : filtered.length === 0 ? (
          <div className="border-t px-6 py-12 text-center text-sm text-muted-foreground">
            {activeFilters ? "No calls match these filters." : "No calls in this period."}
          </div>
        ) : (
          <ul className="divide-y border-t lg:border-t-0">
            {visibleRows.map((c) => {
              const emp = c.employee_id ? empMap.get(c.employee_id) : null;
              const audio = recordingUrl(c);
              // "Not matched" only when the routing map really had no entry; a choice with no
              // branch/department attached (e.g. "Complaint") reads as just that choice.
              const route = routeLabel(c) || c.destination || c.timegroup_name || (c.routing_outcome === "unmatched" ? "Not matched" : null);
              const menu = c.main_menu_selection ? labelForDigit(menuLabels, c.main_menu_selection) : null;
              const where = [menu, route].filter(Boolean).join(" › ") || (c.ivr_path ? "Not matched" : "No choice made");
              const who = emp?.name ?? c.agent_name ?? null;
              // Talk time exists only for an answered call that has ended.
              const talk = isAnsweredStatus(c.status) && !isReallyLive(c) ? formatDuration(c.billable_sec ?? c.duration_sec) : "—";
              const outcome = isReallyLive(c)
                ? <CallStatus status={c.status} live />
                : c.routing_outcome
                ? <span className={cn("inline-flex shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium", routingOutcomeColor[c.routing_outcome])}>{routingOutcomeLabel[c.routing_outcome] ?? c.routing_outcome}</span>
                : <CallStatus status={c.status} />;
              return (
                <li key={c.id} className="px-5 py-3">
                  {/* Phone */}
                  <div className="space-y-1 lg:hidden">
                    <div className="flex items-baseline justify-between gap-3">
                      <Link to="/calls/$id" params={{ id: c.id }} className="truncate font-medium tabular">{formatPhone(c.from_number) ?? "Unknown"}</Link>
                      <span className="shrink-0 text-xs text-muted-foreground tabular">{formatWhen(c.started_at)}</span>
                    </div>
                    <div className="truncate text-sm text-muted-foreground">{where}</div>
                    <div className="flex items-center gap-2 text-sm">
                      {outcome}
                      <span className="min-w-0 flex-1 truncate text-muted-foreground">{who ?? ""}</span>
                      <span className="tabular">{talk}</span>
                      {audio && <RecordingButton id={c.id} />}
                    </div>
                  </div>
                  {/* Desktop */}
                  <div className="hidden grid-cols-[7.5rem_minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1fr)_4rem_7.5rem_2rem] items-center gap-4 lg:grid">
                    <span className="text-sm text-muted-foreground tabular">{formatWhen(c.started_at)}</span>
                    <Link to="/calls/$id" params={{ id: c.id }} className="truncate font-medium tabular hover:text-primary">{formatPhone(c.from_number) ?? "Unknown"}</Link>
                    <span className="truncate text-sm text-muted-foreground">{menu && <span className="text-foreground">{menu}</span>}{menu && route ? " › " : ""}{route}{!menu && !route ? where : null}</span>
                    <span className="truncate text-sm">{who ?? <span className="text-muted-foreground">—</span>}</span>
                    <span className="text-right text-sm tabular">{talk}</span>
                    <span>{outcome}</span>
                    <span>{audio ? <RecordingButton id={c.id} /> : null}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {filtered.length > 50 && (
          <button onClick={() => setShowAll((v) => !v)} className="flex w-full items-center justify-center gap-1 border-t px-5 py-3 text-sm font-medium text-primary hover:bg-muted/40">
            {showAll ? "Show fewer" : `Show all ${filtered.length} calls`} <ChevronRight className={cn("size-4 transition-transform", showAll ? "-rotate-90" : "rotate-90")} />
          </button>
        )}
      </section>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="p-4 md:p-5">
      <div className="truncate text-xs text-muted-foreground">{label}</div>
      {value === null
        ? <Skeleton className="mt-1.5 h-7 w-12" />
        : <div className="mt-0.5 font-display text-2xl font-semibold tabular tracking-tight">{value}</div>}
    </div>
  );
}

function EmptyNote({ children }: { children: React.ReactNode }) {
  return <p className="rounded-xl border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">{children}</p>;
}

/** Ranked horizontal bars: label, count, share of total. Optional click-to-filter. */
function RankList({ rows, total, active, onPick }: {
  rows: { name: string; calls: number; key?: string }[]; total: number; active?: string; onPick?: (key: string) => void;
}) {
  const max = Math.max(1, ...rows.map((r) => r.calls));
  return (
    <ul className="space-y-2.5">
      {rows.map((r) => {
        const pct = total ? Math.round((r.calls / total) * 100) : 0;
        const selected = active && r.key && active === r.key;
        const inner = (
          <>
            <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
              <span className={cn("truncate", selected ? "font-semibold text-primary" : "font-medium")}>{r.name}</span>
              <span className="shrink-0 tabular"><span className="font-semibold">{r.calls}</span> <span className="text-xs text-muted-foreground">{pct}%</span></span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div className={cn("h-full rounded-full transition-[width] duration-500", selected ? "bg-primary" : "bg-primary/70")} style={{ width: `${(r.calls / max) * 100}%` }} />
            </div>
          </>
        );
        return (
          <li key={r.name}>
            {onPick && r.key ? (
              <button onClick={() => onPick(r.key!)} aria-pressed={!!selected} className="-mx-2 block w-[calc(100%+1rem)] rounded-lg px-2 py-1 text-left hover:bg-muted/60">{inner}</button>
            ) : <div>{inner}</div>}
          </li>
        );
      })}
    </ul>
  );
}

function RecordingButton({ id }: { id: string }) {
  return (
    <Button variant="ghost" size="icon" className="size-8" asChild>
      <Link to="/calls/$id" params={{ id }} aria-label="Open call to play the recording" title="Play recording"><Play className="size-3.5" /></Link>
    </Button>
  );
}

function FilterSelect({ label, value, onChange, all, options, className }: {
  label: string; value: string; onChange: (v: string) => void; all: string; options: [string, string][]; className?: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={cn("h-10", value !== "all" && "border-primary/50 text-primary", className)} aria-label={`Filter by ${label.toLowerCase()}`}>
        <SelectValue placeholder={all} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{all}</SelectItem>
        {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
